import { ColumnDef } from "@tanstack/react-table";
import ExpandableCountCell from "./ExpandableCountCell";

interface BuildExpandableCountColumnProps<T> {
  accessorKey: string;
  header: string;
  getCount: (row: T) => number;
  getTotal?: (row: T) => number;
  /** Custom cell label; when omitted, uses count or "count of total". */
  getDisplayText?: (row: T, count: number, total?: number) => string;
  /** When set, controls row expandability separately from display label. */
  getHasItems?: (row: T, count: number, total?: number) => boolean;
  showExpandIcon?: boolean;
  className?: string;
  size?: number;
  minSize?: number;
  autoExpandRowId?: string | null;
  onAutoExpandComplete?: () => void;
  enableSorting?: boolean;
  sortingFn?: ColumnDef<T>["sortingFn"];
  id?: string;
}

/**
 * Helper to create a column that displays a count and toggles row expansion.
 *
 * @example
 * buildExpandableCountColumn({
 *   accessorKey: "sessions",
 *   header: "Sessions",
 *   getCount: (row) => row.sessions?.length || 0
 * })
 *
 * @example
 * // With "of #" format
 * buildExpandableCountColumn({
 *   accessorKey: "requirements",
 *   header: "Requirements",
 *   getCount: (row) => row.completedCount,
 *   getTotal: (row) => row.totalCount
 * })
 * // Displays: "11 of 11" instead of just "11"
 *
 * @example
 * // With auto-expand
 * buildExpandableCountColumn({
 *   accessorKey: "notes",
 *   header: "Notes",
 *   getCount: (row) => row.notes?.length || 0,
 *   autoExpandRowId: highlightBandId
 * })
 */
export function buildExpandableCountColumn<T>({
  accessorKey,
  header,
  getCount,
  getTotal,
  getDisplayText,
  getHasItems,
  showExpandIcon = true,
  className,
  size,
  minSize,
  autoExpandRowId,
  onAutoExpandComplete,
  enableSorting = false,
  sortingFn,
  id
}: BuildExpandableCountColumnProps<T>): ColumnDef<T> {
  return {
    ...(id && { id }),
    accessorKey,
    header,
    enableSorting,
    ...(sortingFn && { sortingFn }),
    size,
    minSize: minSize ?? size,
    cell: ({ row }) => {
      const count = getCount(row.original);
      const total = getTotal ? getTotal(row.original) : undefined;
      const hasItems = getHasItems
        ? getHasItems(row.original, count, total)
        : total !== undefined
          ? total > 0
          : count > 0;

      return (
        <ExpandableCountCell
          row={row}
          count={count}
          total={total}
          hasItems={hasItems}
          showExpandIcon={showExpandIcon}
          className={className}
          autoExpandRowId={autoExpandRowId}
          onAutoExpandComplete={onAutoExpandComplete}
          displayText={getDisplayText?.(row.original, count, total)}
        />
      );
    },
    meta: {
      headerClassName: "max-sm:inline"
    }
  };
}
